import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  FiArrowLeft, 
  FiEdit, 
  FiClock, 
  FiCalendar, 
  FiLayers 
} from "react-icons/fi";

export default function Doc() {
  const navigate = useNavigate();
  const [docDetails, setDocDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const docId = location.state?.docId || null;

  const getDocumentDetails = useCallback(async () => {
    try {
      if (!docId) return null;
      setLoading(true);
      const { data } = await axios.get(`${server}/docs/getById`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          docsId: docId || null,
        },
      });

      setDocDetails(data?.data);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error(e?.response?.data || e.response || "Failed to load document");
    }
  }, [docId]);

  useEffect(() => {
    getDocumentDetails();
  }, [getDocumentDetails]);

  // Extract initials for the profile placeholder circle
  const getInitials = (name) => {
    if (!name || name === "Unknown") return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  // Styled guidelines for rendered rich HTML content
  const richTextStyles = `
    .doc-rich-text h1 { font-size: 1.8em; font-weight: 800; margin-top: 1.4em; margin-bottom: 0.6em; color: #0f172a; border-bottom: 1px solid #f1f5f9; padding-bottom: 0.3em; }
    .doc-rich-text h2 { font-size: 1.4em; font-weight: 700; margin-top: 1.4em; margin-bottom: 0.6em; color: #1e293b; }
    .doc-rich-text h3 { font-size: 1.2em; font-weight: 600; margin-top: 1.4em; margin-bottom: 0.6em; color: #334155; }
    .doc-rich-text p { margin-top: 0.9em; margin-bottom: 0.9em; line-height: 1.75; color: #334155; font-size: 0.95rem; }
    .doc-rich-text ul { list-style-type: disc; padding-left: 1.6em; margin-top: 0.9em; margin-bottom: 0.9em; }
    .doc-rich-text ol { list-style-type: decimal; padding-left: 1.6em; margin-top: 0.9em; margin-bottom: 0.9em; }
    .doc-rich-text li { margin-top: 0.4em; margin-bottom: 0.4em; line-height: 1.6; }
    .doc-rich-text blockquote { border-left: 4px solid #3b82f6; padding-left: 1.25em; color: #64748b; font-style: italic; margin: 1.5em 0; background-color: #f8fafc; padding-top: 0.5em; padding-bottom: 0.5em; border-top-right-radius: 8px; border-bottom-right-radius: 8px; }
    .doc-rich-text pre { background-color: #0f172a; color: #f8fafc; border-radius: 12px; padding: 1.25em; overflow-x: auto; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; font-size: 0.875em; margin: 1.5em 0; box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06); }
    .doc-rich-text code { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; background-color: #f1f5f9; padding: 0.2em 0.4em; border-radius: 6px; font-size: 0.85em; color: #0f172a; font-weight: 500; }
    .doc-rich-text table { width: 100%; border-collapse: collapse; margin: 1.5em 0; font-size: 0.9em; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; }
    .doc-rich-text th { background-color: #f8fafc; border: 1px solid #e2e8f0; padding: 0.75em 1.25em; font-weight: bold; text-align: left; color: #1e293b; }
    .doc-rich-text td { border: 1px solid #e2e8f0; padding: 0.75em 1.25em; color: #475569; }
    .doc-rich-text img { max-width: 100%; height: auto; border-radius: 12px; margin: 1.5em 0; border: 1px solid #e2e8f0; }
  `;

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 font-sans">
      <style>{richTextStyles}</style>
      
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-6xl mx-auto"
      >
        {/* Back navigation */}
        <button
          onClick={() => navigate("../docsList")}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-800 font-medium text-sm transition mb-6 group"
        >
          <FiArrowLeft className="group-hover:-translate-x-1 transition duration-200" />
          Back to documents
        </button>

        {loading ? (
          <div className="bg-white p-16 rounded-2xl border border-slate-100 shadow-sm text-center">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <span className="text-sm text-slate-500 font-medium">Loading document...</span>
          </div>
        ) : !docDetails ? (
          <div className="bg-white p-16 rounded-2xl border border-slate-100 shadow-sm text-center">
            <p className="text-slate-500">Document details not found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-start">
            {/* Left Content Area (Col-span 3) */}
            <div className="lg:col-span-3 space-y-6">
              {/* Document Header Card */}
              <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                {/* Breadcrumbs */}
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 mb-4 uppercase tracking-wider">
                  <span>Documentations</span>
                  <span>/</span>
                  <span className="text-blue-500">{docDetails.category || "General"}</span>
                </div>

                {/* Title */}
                <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-6">
                  {docDetails.title}
                </h1>

                {/* Meta details row */}
                <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-600 text-white font-bold flex items-center justify-center text-sm shadow-sm">
                      {getInitials(docDetails.createdByName)}
                    </div>
                    <div>
                      <span className="block text-sm font-semibold text-slate-800">
                        {docDetails.createdByName || `User #${docDetails.createdBy}`}
                      </span>
                      <span className="flex items-center gap-1.5 text-xs text-slate-400 mt-0.5">
                        <FiCalendar />
                        Published on {new Date(docDetails.createdAt).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric"
                        })}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center px-2.5 py-1 rounded bg-blue-50 text-blue-700 text-xs font-bold font-mono">
                      Version v{docDetails.version}
                    </span>
                    <button
                      onClick={() => navigate("../editDoc", { state: { docId: docDetails.id } })}
                      className="flex items-center gap-2 border border-slate-200 hover:border-slate-300 text-slate-700 px-4 py-2 rounded-xl text-xs font-bold transition shadow-sm hover:shadow active:bg-slate-50"
                    >
                      <FiEdit className="text-sm" />
                      Edit Document
                    </button>
                  </div>
                </div>
              </div>

              {/* Main Document Content */}
              <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm min-h-[300px]">
                <div 
                  className="doc-rich-text"
                  dangerouslySetInnerHTML={{ __html: docDetails.content || "<p className='text-slate-400 italic'>This document is empty.</p>" }}
                />
              </div>
            </div>

            {/* Right Audit/Revision Sidebar (Col-span 1) */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                <div className="flex items-center gap-2 text-slate-800 font-bold text-sm mb-6 border-b border-slate-100 pb-3">
                  <FiClock className="text-blue-500 text-base" />
                  <span>Revision History</span>
                </div>

                {/* Timeline */}
                {docDetails.revisions && docDetails.revisions.length > 0 ? (
                  <div className="relative pl-1">
                    {docDetails.revisions.map((rev, index) => {
                      const isLast = index === docDetails.revisions.length - 1;
                      return (
                        <div key={rev.id || index} className="relative flex gap-4 pb-6 last:pb-0">
                          {/* Connector Line */}
                          {!isLast && (
                            <div className="absolute left-[15px] top-[26px] bottom-0 w-[2px] bg-slate-100" />
                          )}
                          
                          {/* Circle Icon Indicator */}
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center font-bold text-xs text-blue-600 z-10 shadow-sm">
                            v{rev.currentVersion}
                          </div>
                          
                          {/* Revision Content */}
                          <div className="flex-1 min-w-0 pt-0.5">
                            <span className="block text-xs font-bold text-slate-800 truncate">
                              {rev.updatedByName || "Contributor"}
                            </span>
                            <span className="block text-[10px] text-slate-400 font-mono mt-0.5">
                              {rev.updatedAt ? new Date(rev.updatedAt).toLocaleDateString("en-IN", {
                                day: "numeric",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit"
                              }) : "---"}
                            </span>
                            <span className="block text-xs text-slate-500 mt-2 bg-slate-50 p-2 rounded-lg border border-slate-100/50 leading-relaxed italic">
                              "{rev.changeSummary || "Updated document details"}"
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <FiLayers className="text-slate-300 text-2xl mx-auto mb-2" />
                    <p className="text-xs text-slate-400">No revisions recorded.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
